// Pannello di configurazione integrazione FattureInCloud
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Button } from '../../../components/ui/Button'
import { Input } from '../../../components/ui/Input'
import { CredentialsNote } from './CredentialsNote'
import api from '../../../lib/api'

const schema = z.object({
  defaultVatRate: z.coerce.number().min(0).max(100).default(22),
  defaultPaymentMethod: z.string().min(1).default('bonifico'),
  autoCreateOnReportApproval: z.boolean().default(false),
  numerationPrefix: z.string().max(10).default('FE'),
  transmissionFormat: z.enum(['FPR12', 'FPA12']).default('FPR12'),
})

type FormValues = z.infer<typeof schema>

interface FattureInCloudConfigProps {
  currentMetadata?: Record<string, unknown>
  onSaved?: () => void
}

export function FattureInCloudConfig({ currentMetadata, onSaved }: FattureInCloudConfigProps) {
  const qc = useQueryClient()

  const { register, handleSubmit, formState: { errors } } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      defaultVatRate: (currentMetadata?.['defaultVatRate'] as number) ?? 22,
      defaultPaymentMethod: (currentMetadata?.['defaultPaymentMethod'] as string) ?? 'bonifico',
      autoCreateOnReportApproval: (currentMetadata?.['autoCreateOnReportApproval'] as boolean) ?? false,
      numerationPrefix: (currentMetadata?.['numerationPrefix'] as string) ?? 'FE',
      transmissionFormat: (currentMetadata?.['transmissionFormat'] as FormValues['transmissionFormat']) ?? 'FPR12',
    },
  })

  const saveMutation = useMutation({
    mutationFn: async (data: FormValues) => {
      await api.put('/integrations/fattureincloud/config', { metadata: data })
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['integrations'] })
      onSaved?.()
    },
  })

  return (
    <div className="space-y-5">
      <CredentialsNote envVars={['FATTUREINCLOUD_API_KEY', 'FATTUREINCLOUD_COMPANY_ID']} />

      <form onSubmit={handleSubmit((data) => saveMutation.mutate(data))} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-[var(--fc-foreground)] mb-1">
              Aliquota IVA di default (%)
            </label>
            <Input
              type="number"
              min={0}
              max={100}
              {...register('defaultVatRate')}
              error={errors.defaultVatRate?.message}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[var(--fc-foreground)] mb-1">
              Prefisso numerazione fatture
            </label>
            <Input
              type="text"
              maxLength={10}
              placeholder="FE"
              {...register('numerationPrefix')}
              error={errors.numerationPrefix?.message}
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-[var(--fc-foreground)] mb-1">
            Metodo di pagamento di default
          </label>
          <select
            className="w-full rounded-md border border-[var(--fc-border)] bg-[var(--fc-background)] px-3 py-2 text-sm text-[var(--fc-foreground)]"
            {...register('defaultPaymentMethod')}
          >
            <option value="bonifico">Bonifico bancario</option>
            <option value="contanti">Contanti</option>
            <option value="carta">Carta di credito</option>
            <option value="rid">RID/SEPA Direct Debit</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-[var(--fc-foreground)] mb-1">
            Formato trasmissione SDI
          </label>
          <select
            className="w-full rounded-md border border-[var(--fc-border)] bg-[var(--fc-background)] px-3 py-2 text-sm text-[var(--fc-foreground)]"
            {...register('transmissionFormat')}
          >
            <option value="FPR12">FPR12 — Fattura verso privati (B2B/B2C)</option>
            <option value="FPA12">FPA12 — Fattura verso PA</option>
          </select>
        </div>

        <div className="flex items-start gap-2">
          <input
            type="checkbox"
            id="autoCreateOnReportApproval"
            className="mt-0.5 h-4 w-4 rounded border-[var(--fc-border)]"
            {...register('autoCreateOnReportApproval')}
          />
          <div>
            <label htmlFor="autoCreateOnReportApproval" className="text-sm text-[var(--fc-foreground)]">
              Crea fattura automaticamente all'approvazione rapportino
            </label>
            <p className="text-xs text-[var(--fc-foreground-muted)]">
              Se abilitato, quando un rapportino viene approvato viene creata automaticamente
              una fattura su FattureInCloud.
            </p>
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <Button type="button" variant="secondary" onClick={onSaved}>
            Annulla
          </Button>
          <Button type="submit" isLoading={saveMutation.isPending}>
            Salva configurazione
          </Button>
        </div>
      </form>
    </div>
  )
}
